<template>
    <div class="telekom-angebote-mobilfunk">
        <BasicBreadcrumb
            :items="[
                { label: 'Telekom Angebote', to: `/markt/${storeslug}/telekom-angebote` },
                { label: 'Handy mit Vertrag', to: '' }
            ]"
        />
        <div class="lg:container mx-auto p-8 lg:px-0 tariffSlider">
            <ContentHeroBanner
                v-for="banner in heroBanner?.items" :key="banner?.id"
                :img-mobile="banner?.imageUrlMobile"
                :img-desktop="banner?.imageUrl"
                :title="banner?.title"
                :background-img-mobile="backgroundImgMobile"
                :background-img-desktop="backgroundImgDesktop"
            />
            <div class="flex sm:flex-row flex-col justify-center gap-4 p-4">
                <a href="#telekom" class="rounded-3xl bg-secondary px-4 py-2 flex items-center border border-gray-200">
                    <span class="text-primary font-bold px-2 text-xl">➝</span>zu den Telekom Tarifangeboten
                </a>
                <a href="#congstar" class="rounded-3xl bg-secondary px-4 py-2 flex items-center border border-gray-200">
                    <span class="text-primary font-bold px-2 text-xl">➝</span>zu den congstar Tarifangeboten
                </a>
            </div>
            <div class="py-2">
                <BasicBtErrorBoundary>
                    <TariffSlider
                        :key="Date.now()"
                        :tenant-id="tenantId"
                        :sub-tenant="false"
                        :title="'Unsere Telekom Tarife'"
                        :store="currentStore"
                    />
                </BasicBtErrorBoundary>
            </div>
            <div>
                <a id="telekom" name="telekom"/>
                <div class="pb-8">
                    <div v-if="hasTariffs">
                        <BasicBtErrorBoundary>
                            <TariffTenantOfferList
                                :key="Date.now()"
                                :tenant-id="tenantId"
                                :mobile="true"
                                :sub-tenant="false"
                                :title="'Unsere Telekom Tarifangebote mit Handy'"
                                :store="currentStore"
                                :link-path="''"
                                :link-text="'zu unseren Tarifangeboten'"
                            />
                        </BasicBtErrorBoundary>
                        <BasicBtErrorBoundary>
                            <TariffTenantOfferList
                                :key="Date.now()"
                                :tenant-id="tenantId"
                                :mobile="true"
                                :sub-tenant="false"
                                :refurbished-highlights="true"
                                :title="'Unsere Telekom Refurbished Highlights'"
                                :store="currentStore"
                                :link-path="''"
                                :link-text="'zu unseren Tarifangeboten'"
                            />
                        </BasicBtErrorBoundary>
                    </div>
                    <h1 v-else class="container mx-auto font-bold text-paragraph text-2xl text-center">
                        Aktuell gibt es keine Telekom Tarifangebote
                    </h1>
                </div>
            </div>
            <div class="py-2">
                <BasicBtErrorBoundary>
                    <TariffSlider
                        :tenant-id="tenantId"
                        :sub-tenant="true"
                        :title="'Unsere congstar Tarife'"
                        :store="currentStore"
                    />
                </BasicBtErrorBoundary> 
            </div>
            <div>
                <a id="congstar" name="congstar"/>
                <div class="pb-8">
                    <div v-if="hasTariffs">
                        <BasicBtErrorBoundary>
                            <TariffTenantOfferList
                                :key="Date.now()"
                                :tenant-id="tenantId"
                                :mobile="true"
                                :sub-tenant="true"
                                :title="'Unsere congstar Tarifangebote mit Handy'"
                                :store="currentStore"
                                :link-path="''"
                                :link-text="'zu unseren Tarifangeboten'"
                            />
                        </BasicBtErrorBoundary>
                        <BasicBtErrorBoundary>
                            <TariffTenantOfferList
                                :key="Date.now()"
                                :tenant-id="tenantId"
                                :mobile="true"
                                :sub-tenant="true"
                                :refurbished-highlights="true"
                                :title="'Unsere congstar Refurbished Highlights'"
                                :store="currentStore"
                                :link-path="''"
                                :link-text="'zu unseren Tarifangeboten'"
                            />
                        </BasicBtErrorBoundary>
                    </div>
                    <h1
                        v-else
                        class="container mx-auto font-bold text-paragraph text-2xl text-center"
                    >
                        Aktuell gibt es keine congstar Tarifangebote
                    </h1>
                </div>
            </div>
            <div class="pt-4 pb-12 container mx-auto font-bold text-paragraph text-2xl text-center">
                Alle Informationen und Angebote zu den Themen MagentaTV,<br>
                Internet, Glasfaser und Kombipakete findest du hier
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-8 pb-8">
                <TariffSubPageTeaser
                    :background="backgroundTile"
                    :icon="iconMagenta"
                    url="/telekom-angebote/magentatv"
                    :title="'MagentaTV'"
                />
                <TariffSubPageTeaser
                    :background="backgroundTile"
                    :icon="iconInternet"
                    url="/telekom-angebote/internet"
                    :title="'Internet'"
                />
                <TariffSubPageTeaser
                    :background="backgroundTile"
                    :icon="iconOptical"
                    url="/telekom-angebote/glasfaser"
                    :title="'Glasfaser'"
                />
                <TariffSubPageTeaser
                    :background="backgroundTile"
                    :icon="iconCombi"
                    url="/telekom-angebote/kombipakete"
                    :title="'Kombi-Pakete'"
                />
            </div>
        </div>
        <TariffEyeCatcher />
    </div>
</template>

<script setup lang="ts">
    import { hasTenantTariffs } from "~/composables/tariffs";
    import type { ContentBlock } from '~~/models/Tariff/ContentBlock';
    import { ContentBlockType } from '~~/models/Tariff/ContentBlock';

    import backgroundImgMobile from '~/assets/img/tariff-topic/telekom/ssb_BG_mobile.png';
    import backgroundImgDesktop from '~/assets/img/tariff-topic/telekom/ssb_BG.png';
    import backgroundTile from '~/assets/img/tariff-topic/telekom/bg_icons.png';
    import iconMagenta from '~/assets/img/tariff-topic/telekom/two/magentaTV.png';
    import iconInternet from '~/assets/img/tariff-topic/telekom/two/internet.png';
    import iconOptical from '~/assets/img/tariff-topic/telekom/two/glasfaser.png';
    import iconCombi from '~/assets/img/tariff-topic/telekom/two/kombi-pakete.png';

    const tenantId: string = "692ef56ad1b19b630ac62ff3";
    const currentStore = useCurrentStore();
    const hasTariffs = ref<boolean>();

    const route = useRoute();
    const storeslug = route.params.storeslug;    

    const storeName = currentStore?.value?.name ?? 'expert';
    const pageTitle = 'Telekom Mobilfunk bei ' + storeName + ' | MagentaMobil Tarife & Deals bei expert';
    const pageDescription = 'Telekom Mobilfunk bei ' + storeName + ': MagentaMobil Tarife, Prepaid & Young Angebote mit top Smartphones und bis zu 240€ Cashback. Jetzt sichern!';
    useHead({
        title: pageTitle,
        meta: [{ name: 'description', content: pageDescription }
        ]
    });     
    
    const { data: heroBanner } = await useFetch<{ items: ContentBlock[] }>(
        `/api/nea/tariff-service/contentBlock`,
        {
            query: {
                page: 0,
                validOnly: true,
                pageSize: 1,
                type: ContentBlockType.TELEKOM_MOBILE_BANNER
            }
        }
    );  

    const checkHasTariffs = async (storeId: string): Promise<void> => {
        console.log(tenantId, storeId)
        hasTariffs.value = await hasTenantTariffs(tenantId, storeId, {mobile: true, subTenant: true});
    };

    await checkHasTariffs(currentStore.value?.id);
    await checkStoreHasPage(tenantId);

    watch(currentStore, async (store) => {
        const storeId: string = store?.id;
        if (!storeId) {
            return;
        }
        await checkHasTariffs(storeId);
        await checkStoreHasPage(tenantId);
    });
</script>
<style lang="postcss">

    .tariffSlider .vueperslides--fixed-height.vueperslides--bullets-outside {
        margin-bottom: 1em;
    }

</style>